import { ontologyFieldDefinitionSchema } from 'features/dynamicOntology'
import { z } from 'zod'

export enum FieldCategory {
  INPUT = 'input',
  QUESTION = 'question',
  FLAG = 'flag',
  OUTCOME = 'outcome',
}

export enum OutcomeStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

// Regular form input field - extends base ontology schema
const InputFieldSchema = ontologyFieldDefinitionSchema.extend({
  category: z.literal(FieldCategory.INPUT),
  defaultValue: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.array(z.number()),
      z.array(z.boolean()),
    ])
    .optional(),
  /**
   * Optional tooltip configuration object.
   * When present, automatically shows an info icon next to the field.
   */
  tooltip: z
    .object({
      /**
       * The tooltip content format
       */
      format: z.enum(['markdown', 'html', 'text']).default('markdown'),
      /**
       * The actual tooltip content
       */
      content: z.string(),
      /**
       * Tooltip positioning preference
       */
      placement: z
        .enum([
          'top',
          'bottom',
          'left',
          'right',
          'topLeft',
          'topRight',
          'bottomLeft',
          'bottomRight',
        ])
        .default('top'),
      /**
       * Tooltip trigger behavior
       */
      trigger: z.enum(['hover', 'click', 'focus']).default('hover'),
    })
    .optional(),
  /**
   * Optional weight for weighted scoring in computed sections.
   * Used by functions like computeWeightedBooleanScore.
   */
})

// Flowchart question node
const QuestionNodeSchema = z.object({
  category: z.literal(FieldCategory.QUESTION),
  name: z.string(),
  type: z.enum(['boolean', 'number', 'text', 'enum', 'date']),
  description: z.string().optional(),
  allowedValues: z.array(z.string()).optional(),
  defaultValue: z
    .union([z.string(), z.number(), z.boolean(), z.null()])
    .optional(),
})

// Flowchart flag node
// Note: defaultValue is unused for Flag nodes but included for type consistency
const FlagNodeSchema = z.object({
  category: z.literal(FieldCategory.FLAG),
  name: z.string(),
  severity: z.enum(['info', 'warning', 'error']),
  description: z.string().optional(),
  defaultValue: z.unknown().optional(),
})

// Flowchart outcome node
// Note: defaultValue is unused for Outcome nodes but included for type consistency
const OutcomeNodeSchema = z.object({
  category: z.literal(FieldCategory.OUTCOME),
  name: z.string(),
  result: z.unknown(),
  description: z.string().optional(),
  defaultValue: z.unknown().optional(),
  status: z
    .enum([
      OutcomeStatus.SUCCESS,
      OutcomeStatus.ERROR,
      OutcomeStatus.WARNING,
      OutcomeStatus.INFO,
    ])
    .optional()
    .default(OutcomeStatus.SUCCESS),
})

// TODO: Remove once we backfill all ontology fields with a category
// Preprocess to default missing category to INPUT for backwards compatibility
export const OntologyFieldSchema = z.preprocess(
  (val) => {
    if (typeof val === 'object' && val !== null && !('category' in val)) {
      return { ...val, category: FieldCategory.INPUT }
    }
    return val
  },
  z.discriminatedUnion('category', [
    InputFieldSchema,
    QuestionNodeSchema,
    FlagNodeSchema,
    OutcomeNodeSchema,
  ])
)

export type InputField = z.infer<typeof InputFieldSchema>
export type QuestionNode = z.infer<typeof QuestionNodeSchema>
export type FlagNode = z.infer<typeof FlagNodeSchema>
export type OutcomeNode = z.infer<typeof OutcomeNodeSchema>
export type OntologyField = z.infer<typeof OntologyFieldSchema>

// Flowchart-specific node types (excludes INPUT)
export type FlowchartNode = QuestionNode | FlagNode | OutcomeNode

export const FieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  z.array(z.boolean()),
  z.array(z.record(z.string(), z.unknown())),
  z.record(z.string(), z.unknown()),
  z.undefined(),
])

export type FieldValue = z.infer<typeof FieldValueSchema>

/**
 * Field values record - maps field IDs to their values
 * This is the shape of ticket.fieldValues
 */
export const FieldValuesSchema = z.record(z.string(), FieldValueSchema)

export type FieldValues = z.infer<typeof FieldValuesSchema>
